/**
 * TableSyncManager - Orchestrates multi-table data synchronization
 *
 * This service manages sending edits to the correct Google Sheets using
 * dynamic sheet discovery. It handles:
 * - Grouping edits by source table
 * - Automatic sheet discovery and creation
 * - Parallel updates to multiple sheets
 * - Simplified configuration-free operation
 */

import { dynamicSheetManager, type TableUpdate } from './dynamic-sheet-manager';
import type { UpdatedEntry } from '../types';

/**
 * Result of syncing a single table
 */
export interface TableSyncResult {
  /** Name of the Unity string table */
  tableName: string;
  /** Sync status */
  status: 'success' | 'error';
  /** Number of entries that were updated */
  updatedCount: number;
  /** Total number of entries attempted */
  totalCount: number;
  /** Error message if status is 'error' */
  error?: string;
  /** Additional details about the operation */
  details?: string;
}

/**
 * Overall sync operation result
 */
export interface MultiTableSyncResult {
  /** Results for each table */
  tableResults: TableSyncResult[];
  /** Overall operation status */
  overallStatus: 'success' | 'partial' | 'failed';
  /** Total entries updated across all tables */
  totalUpdated: number;
  /** Total entries attempted across all tables */
  totalAttempted: number;
  /** Summary message */
  summary: string;
}

/**
 * Grouped edits by table name
 */
interface GroupedEdits {
  [tableName: string]: UpdatedEntry[];
}

/**
 * Service for managing multi-table synchronization
 */
class TableSyncManagerService {
  private static instance: TableSyncManagerService;

  private constructor() {}

  public static getInstance(): TableSyncManagerService {
    if (!TableSyncManagerService.instance) {
      TableSyncManagerService.instance = new TableSyncManagerService();
    }
    return TableSyncManagerService.instance;
  }

  /**
   * Sync edits to multiple Google Sheets using dynamic sheet discovery
   */
  public async syncEditsByTable(
    updatedEntries: UpdatedEntry[],
    currentLanguage: string
  ): Promise<MultiTableSyncResult> {
    console.log('[TableSyncManager] Starting multi-table sync operation');
    console.log(`[TableSyncManager] Processing ${updatedEntries.length} entries`);

    const results: TableSyncResult[] = [];
    let totalUpdated = 0;
    let totalAttempted = 0;

    try {
      // Group entries by table
      const groupedEdits = this.groupEditsByTable(updatedEntries);

      if (Object.keys(groupedEdits).length === 0) {
        return {
          tableResults: [],
          overallStatus: 'success',
          totalUpdated: 0,
          totalAttempted: 0,
          summary: 'No edits to sync'
        };
      }

      console.log(`[TableSyncManager] Found edits for ${Object.keys(groupedEdits).length} tables`);

      // Process each table's edits using dynamicSheetManager
      const syncPromises = Object.entries(groupedEdits).map(([tableName, entries]) =>
        this.syncSingleTable(tableName, entries, currentLanguage)
      );

      // Wait for all table syncs to complete
      const tableResults = await Promise.allSettled(syncPromises);

      // Process results
      tableResults.forEach((result, index) => {
        const tableName = Object.keys(groupedEdits)[index];
        const entryCount = groupedEdits[tableName].length;

        if (result.status === 'fulfilled') {
          results.push(result.value);
          totalUpdated += result.value.updatedCount;
          totalAttempted += result.value.totalCount;
        } else {
          console.error(`[TableSyncManager] Failed to sync table ${tableName}:`, result.reason);

          results.push({
            tableName,
            status: 'error',
            updatedCount: 0,
            totalCount: entryCount,
            error: result.reason instanceof Error ? result.reason.message : String(result.reason)
          });
          totalAttempted += entryCount;
        }
      });

      // Determine overall status
      const successCount = results.filter(r => r.status === 'success').length;
      const errorCount = results.filter(r => r.status === 'error').length;

      let overallStatus: 'success' | 'partial' | 'failed';
      let summary: string;

      if (errorCount === 0) {
        overallStatus = 'success';
        summary = `Successfully updated ${totalUpdated} entries across ${successCount} tables`;
      } else if (successCount > 0) {
        overallStatus = 'partial';
        summary = `Updated ${totalUpdated} entries across ${successCount} tables. ${errorCount} tables failed`;
      } else {
        overallStatus = 'failed';
        summary = `Failed to update any tables. ${errorCount} tables failed`;
      }

      console.log(`[TableSyncManager] Sync completed: ${summary}`);

      return {
        tableResults: results,
        overallStatus,
        totalUpdated,
        totalAttempted,
        summary
      };

    } catch (error) {
      console.error('[TableSyncManager] Critical error during sync:', error);

      return {
        tableResults: results,
        overallStatus: 'failed',
        totalUpdated,
        totalAttempted,
        summary: error instanceof Error ? error.message : 'Unknown sync error'
      };
    }
  }

  /**
   * Group updated entries by their source string table
   */
  private groupEditsByTable(updatedEntries: UpdatedEntry[]): GroupedEdits {
    const grouped: GroupedEdits = {};

    for (const entry of updatedEntries) {
      // Determine table name from the entry
      const tableName = this.determineTableForEntry(entry);

      if (!tableName) {
        console.warn(`[TableSyncManager] Could not determine table for entry: ${entry.key || entry.id}`);
        continue;
      }

      if (!grouped[tableName]) {
        grouped[tableName] = [];
      }

      grouped[tableName].push(entry);
    }

    return grouped;
  }

  /**
   * Determine which table an entry belongs to based on its properties
   */
  private determineTableForEntry(entry: UpdatedEntry): string | null {
    // If the entry has explicit table information, use it
    if ((entry as any).tableName) {
      return (entry as any).tableName;
    }

    // For now, assume a default table name that will be dynamically managed
    // The dynamic sheet manager will handle finding or creating the appropriate sheet
    return 'MainStringTable';
  }

  /**
   * Sync a single table's edits using dynamic sheet manager
   */
  private async syncSingleTable(
    tableName: string,
    entries: UpdatedEntry[],
    currentLanguage: string
  ): Promise<TableSyncResult> {
    console.log(`[TableSyncManager] Syncing table "${tableName}" with ${entries.length} entries`);

    const result: TableSyncResult = {
      tableName,
      status: 'error',
      updatedCount: 0,
      totalCount: entries.length
    };

    try {
      // Convert UpdatedEntry[] to TableUpdate[]
      const updates: TableUpdate[] = entries.map(entry => ({
        key: entry.key || entry.id, // Use key if available, fallback to id
        language: entry.language || currentLanguage, // Use entry's language if available, fallback to currentLanguage
        value: entry.text
      }));

      // Deduplicate updates before sending to Google Sheets
      const deduplicatedUpdates = this.deduplicateUpdatesByKey(updates);

      console.log(`[TableSyncManager] Deduplicated ${updates.length} updates to ${deduplicatedUpdates.length} unique (key, language) pairs`);

      // Use dynamic sheet manager to handle the update
      const updateResult = await dynamicSheetManager.updateTableData(tableName, deduplicatedUpdates);

      if (updateResult.success) {
        result.status = 'success';
        result.updatedCount = updateResult.affectedCount;
        result.details = updateResult.details;
        console.log(`[TableSyncManager] Successfully synced table "${tableName}"`);
      } else {
        result.error = updateResult.error;
        result.details = updateResult.details;
        console.error(`[TableSyncManager] Failed to sync table "${tableName}": ${updateResult.error}`);
      }

    } catch (error) {
      console.error(`[TableSyncManager] Failed to sync table "${tableName}":`, error);
      result.error = error instanceof Error ? error.message : 'Unknown error';
    }

    return result;
  }

  /**
   * Deduplicate updates by (key, language) pair
   *
   * When multiple GameObjects share the same localization key,
   * we only need to update the Google Sheet once per unique (key, language) pair.
   * This method keeps the LAST occurrence of each unique pair, assuming it's the
   * most recent edit if the user made multiple changes.
   *
   * @param updates - Array of table updates that may contain duplicates
   * @returns Deduplicated array with one update per (key, language) pair
   */
  private deduplicateUpdatesByKey(updates: TableUpdate[]): TableUpdate[] {
    // Use a Map to track unique (key, language) combinations
    // Key format: "key|language"
    const uniqueUpdates = new Map<string, TableUpdate>();

    for (const update of updates) {
      // Create composite key for deduplication
      const compositeKey = `${update.key}|${update.language}`;

      // Store or overwrite - keeping the last occurrence
      uniqueUpdates.set(compositeKey, update);
    }

    const deduplicated = Array.from(uniqueUpdates.values());

    // Log deduplication statistics if any duplicates were found
    if (updates.length > deduplicated.length) {
      console.log(`[TableSyncManager] Removed ${updates.length - deduplicated.length} duplicate updates`);
      console.log(`[TableSyncManager] Original: ${updates.length} updates, Deduplicated: ${deduplicated.length} updates`);
    }

    return deduplicated;
  }

  /**
   * Get sync status for all discovered tables
   */
  public async getTableSyncStatuses(): Promise<{ [tableName: string]: 'idle' | 'syncing' | 'error' | 'success' }> {
    const statuses: { [tableName: string]: 'idle' | 'syncing' | 'error' | 'success' } = {};

    try {
      const allTables = await dynamicSheetManager.getAllTables();
      allTables.forEach(table => {
        statuses[table.tableName] = 'idle'; // Default status
      });
    } catch (error) {
      console.error('[TableSyncManager] Failed to get table sync statuses:', error);
    }

    return statuses;
  }
}

// Export singleton instance
export const tableSyncManager = TableSyncManagerService.getInstance();
